import React, { useState } from 'react';
import { Table, Spin, Descriptions, Button, Form, Space, Input, InputNumber, DatePicker, Select, Alert, Tag, Divider, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { FileSearchOutlined, FormOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import axios from 'axios';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../../Models/Utilisateur';
import Data from '../../../../Data/Data.json';
import { Crypter } from '../../../../Functions/Security/Crypter';
import { Box, Typography } from '@mui/material';
import SearchInput from '../../../SearchInput';
import SearchModal from '../../../SearchModal';
import Params from '../../../../Types/ParamsPicklist';
import formatNumber from '../../../../Functions/Traitement/FormatNumber';
import { useTypePret, ruleMaxValue } from '../../../TypePretInfoModal';

/**
 * Restructuration — Analyse : dossier + scenarios de renegociation.
 * Workflow : dossier BROUILLON -> scenarios -> SOUMETTRE (EN ATTENTE).
 */
const AnalyseRestructuration = () => {
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();
    const { typePret, loadTypePret, typePretModal } = useTypePret();

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [pret, setPret] = useState<any>(null);
    const [echeances, setEcheances] = useState<any[]>([]);
    const [dossier, setDossier] = useState<any>(null);        // dossier BROUILLON courant
    const [scenSel, setScenSel] = useState<React.Key | null>(null);
    const [preview, setPreview] = useState<any[]>([]);

    let searchPret: Params = { CodeAgence: '', Table: "PRETS", Colonne1: "NUMDOS", Colonne2: "NOMCLI", Colagence: '101', Choix: 22 };
    let searchPeriod: Params = { CodeAgence: '', Table: "v_period_lib", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: '101', Choix: 22 };
    let searchMotif: Params = { CodeAgence: '', Table: "v_motif_restru", Colonne1: "CODMOTIF", Colonne2: "LIBMOTIF", Colagence: '101', Choix: 22 };

    // ---------------- Appel API generique ----------------
    function callApi(action: string, extra: any = {}, onSuccess?: (res: any) => void, silent = false) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.RestructurePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action,
                numDos: form.getFieldValue('NUMDOS') ?? '',
                utiVal: user.coduti,
                codAge: user.codage,
                ...extra
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    if (onSuccess) onSuccess(res);
                    else if (!silent) SweetAlert.fire({ title: res.Message, icon: "success" });
                } else if (!silent) {
                    SweetAlert.fire({ title: res.Message, icon: "warning" });
                }
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    // ---------------- Consultation du pret ----------------
    function consult() {
        const numdos = form.getFieldValue('NUMDOS');
        if (!numdos) { SweetAlert.fire({ title: "Sélectionnez un dossier de prêt", icon: "warning" }); return; }
        setLoading(true);
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.GetPretConsult,
            headers: { 'Authorization': authHeader(), 'CipherNumDos': Crypter.encrypt(numdos) }
        })
            .then((response) => {
                if (!response.data) { setLoading(false); return; }
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    const p = res.Result?.Table?.[0] ?? null;
                    setPret(p);
                    setEcheances(res.Result?.Table1 ?? []);
                    // Config du type de pret -> champs autorises/verrouilles
                    if (p?.TYPEPRET) loadTypePret(p.TYPEPRET);
                    // Prefill des champs non renégociables avec les valeurs du pret
                    form.setFieldsValue({
                        TAUXINT_SCEN: p?.TAUXINT,
                        TAUXPEN_SCEN: p?.TAUXPEN,
                        TYPAMORT_SCEN: p?.TYPAMORT,
                        NBRECHE_SCEN: p?.NBRECHE,
                        PERIODIC_SCEN: p?.PERIODIC
                    });
                    loadDossier(numdos);
                } else {
                    setPret(null);
                    SweetAlert.fire({ title: res.Message, icon: "warning" });
                }
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    // Charge un dossier BROUILLON/EN ATTENTE existant du dossier selectionne
    function loadDossier(numdos: string) {
        callApi('LISTE', { numDos: numdos, cdEtat: 'BROUILLON' }, (res) => {
            const rows = (res.Result?.Table ?? []).filter((r: any) => r.NUMDOS === numdos);
            const d = rows[0] ?? null;
            setDossier(d);
            setScenSel(null);
            if (d) {
                form.setFieldsValue({ MOTIF: d.MOTIF, lib_MOTIF: d.MOTIF, DIAGNOSTIC: d.DIAGNOSTIC, COMMENTAIRE: d.COMMENTAIRE });
                callApi('LISTESCEN', { idRestru: d.Id }, (r) => setScenarios(r.Result?.Table ?? []), true);
            } else {
                setScenarios([]);
            }
        }, true);
    }

    const [scenarios, setScenarios] = useState<any[]>([]);

    // ---------------- Preview capital (non persiste) ----------------
    function previewCapital(thenConfirm: () => void) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.RestructurePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action: 'CAPITAL', numDos: form.getFieldValue('NUMDOS'),
                mntDecondonne: form.getFieldValue('MNTDECONDONNE'),
                utiVal: user.coduti, codAge: user.codage
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode != 200) { SweetAlert.fire({ title: res.Message, icon: "warning" }); return; }
                const c = res.Result?.Table?.[0];
                SweetAlert.fire({
                    title: "Confirmer l'action ?",
                    html: `<table style="margin:auto;text-align:left;font-size:14px">
                        <tr><td>Solde capital restant</td><td style="text-align:right"><b>${formatNumber(c?.soldeCapital ?? 0)}</b></td></tr>
                        <tr><td>+ Intérêts capitalisés</td><td style="text-align:right"><b>${formatNumber(c?.interetsCapitalises ?? 0)}</b></td></tr>
                        <tr><td>+ Pénalités capitalisées</td><td style="text-align:right"><b>${formatNumber(c?.penalitesCapitalisees ?? 0)}</b></td></tr>
                        <tr><td>− Remise décondonnée</td><td style="text-align:right"><b>${formatNumber(c?.remiseDecondonnee ?? 0)}</b></td></tr>
                        <tr style="border-top:1px solid #116599"><td><b>= Nouveau capital</b></td><td style="text-align:right;color:#116599"><b>${formatNumber(c?.nouveauCapital ?? 0)}</b></td></tr>
                        <tr><td>Frais de restructuration${c?.codOpe ? ` (${c.codOpe})` : ''}</td><td style="text-align:right"><b>${formatNumber(c?.frais ?? 0)}</b></td></tr>
                    </table>`,
                    showCancelButton: true, confirmButtonText: "Confirmer", cancelButtonText: "Annuler",
                    confirmButtonColor: "#116599", icon: 'question', width: 500
                }).then((r) => { if (r.isConfirmed) thenConfirm(); });
            })
            .catch(() => setLoading(false));
    }

    // ---------------- Actions ----------------
    function handleDossier() {
        form.validateFields(['MOTIF']).then(() => {
            callApi('DOSSIER', {
                motif: form.getFieldValue('MOTIF'),
                diagnostic: form.getFieldValue('DIAGNOSTIC'),
                coment: form.getFieldValue('COMMENTAIRE')
            }, (res) => {
                const id = res.Result?.Table?.[res.Result.Table.length - 1]?.idRestru;
                if (id) { setDossier({ ...dossier, Id: id }); loadDossier(form.getFieldValue('NUMDOS')); }
                SweetAlert.fire({ title: res.Message, icon: "success" });
            });
        }).catch(() => SweetAlert.fire({ title: "Motif obligatoire", icon: "warning" }));
    }

    function scenarioPayload() {
        const d = form.getFieldValue('DAT1ECHE_SCEN');
        return {
            idRestru: dossier?.Id,
            idScen: scenSel ?? null,
            libScen: form.getFieldValue('LIBSCEN'),
            nbrEche: form.getFieldValue('NBRECHE_SCEN'),
            periodic: form.getFieldValue('PERIODIC_SCEN'),
            dat1Eche: dayjs.isDayjs(d) ? d.format('YYYY-MM-DD') : d,
            tauxInt: form.getFieldValue('TAUXINT_SCEN') ?? null,
            tauxPen: form.getFieldValue('TAUXPEN_SCEN') ?? null,
            typAmort: form.getFieldValue('TYPAMORT_SCEN') ?? null,
            mntDecondonne: form.getFieldValue('MNTDECONDONNE') ?? 0,
            coment: form.getFieldValue('COMENTSCEN')
        };
    }

    function handleScenario() {
        if (!dossier?.Id) { SweetAlert.fire({ title: "Créez d'abord le dossier", icon: "warning" }); return; }
        form.validateFields(['LIBSCEN', 'NBRECHE_SCEN', 'PERIODIC_SCEN', 'DAT1ECHE_SCEN'])
            .then(() => previewCapital(() =>
                callApi('SCENARIO', scenarioPayload(), (res) => {
                    SweetAlert.fire({ title: res.Message, icon: "success" });
                    callApi('LISTESCEN', { idRestru: dossier.Id }, (r) => setScenarios(r.Result?.Table ?? []), true);
                    setScenSel(null);
                })))
            .catch(() => SweetAlert.fire({ title: "Vérifiez les champs du scénario", icon: "warning" }));
    }

    function handlePreview() {
        form.validateFields(['NBRECHE_SCEN', 'PERIODIC_SCEN', 'DAT1ECHE_SCEN'])
            .then(() => callApi('PREVIEW', scenarioPayload(), (res) => {
                const tables = res.Result ?? {};
                const ecp = Object.values(tables)
                    .find((t: any) => Array.isArray(t) && t.length > 0 && t[0].numSeq !== undefined);
                setPreview((ecp as any[]) ?? []);
            }))
            .catch(() => SweetAlert.fire({ title: "Vérifiez les champs du scénario", icon: "warning" }));
    }

    function handleSoumettre() {
        if (!dossier?.Id) { SweetAlert.fire({ title: "Aucun dossier à soumettre", icon: "warning" }); return; }
        SweetAlert.fire({
            title: "Soumettre le dossier de restructuration ?",
            text: "Le dossier passera EN ATTENTE pour décision de l'organe compétent.",
            showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non",
            confirmButtonColor: "#116599", icon: 'question'
        }).then((r) => {
            if (r.isConfirmed)
                callApi('SOUMETTRE', { idRestru: dossier.Id }, (res) => {
                    SweetAlert.fire({ title: res.Message, icon: "success" });
                    consult();
                });
        });
    }

    // ---------------- Colonnes ----------------
    const colsScen: ColumnsType<any> = [
        { title: 'Id', dataIndex: 'Id', width: 50 },
        { title: 'Scénario', dataIndex: 'LIBSCEN' },
        { title: 'Nb éch.', dataIndex: 'NBRECHE', width: 70 },
        { title: 'Pério.', dataIndex: 'PERIODIC', width: 70 },
        { title: '1ère éch.', dataIndex: 'DAT1ECHE', width: 105, render: (v: any) => v ? String(v).substring(0, 10) : '' },
        { title: 'Taux négocié', dataIndex: 'TAUXINT', width: 90, render: (v: any) => v ?? '—' },
        { title: 'Type', dataIndex: 'TYPAMORT', width: 55, render: (v: any) => v ?? '—' },
        { title: 'Nouv. capital', dataIndex: 'MNTCAPIT', render: (v: number) => formatNumber(v) },
        { title: 'Frais', dataIndex: 'MTNFRAIS', width: 80, render: (v: number) => formatNumber(v ?? 0) },
        { title: 'Version', dataIndex: 'VERSIONECP', width: 70 },
    ];

    const colsEchActuel: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'NUMSEQ', width: 55 },
        { title: 'Date', dataIndex: 'DATECH', width: 105, render: (v: any) => v ? String(v).substring(0, 10) : '' },
        { title: 'Capital restant', dataIndex: 'MTNCRD', render: (v: number) => formatNumber(v) },
        { title: 'Amort.', dataIndex: 'AMMORT', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'INTERE', render: (v: number) => formatNumber(v) },
        { title: 'Annuité', dataIndex: 'ANNUIT', render: (v: number) => formatNumber(v) },
        {
            title: 'État', dataIndex: 'PAYEOK', width: 85,
            render: (v: any, e: any) => (v === 1 || v === true) ? <Tag color="green">Payée</Tag>
                : (e.IMPAYE === 1 || e.IMPAYE === true) ? <Tag color="red">Impayée</Tag>
                : <Tag>À venir</Tag>
        },
    ];

    const colsEch: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'numSeq', width: 55 },
        { title: 'Date', dataIndex: 'dateEcheacnce', width: 105 },
        { title: 'Capital', dataIndex: 'capital', render: (v: number) => formatNumber(v) },
        { title: 'Amort.', dataIndex: 'amortissement', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'interet', render: (v: number) => formatNumber(v) },
        { title: 'Annuité HT', dataIndex: 'anHT', render: (v: number) => formatNumber(v) },
        { title: 'Annuité TTC', dataIndex: 'anTTC', render: (v: number) => formatNumber(v) },
    ];

    const tabsItems: TabsProps['items'] = [
        {
            key: '1',
            label: <span><FileSearchOutlined /> Prêt concerné</span>,
            children: (
                <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                    <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Prêt à restructurer</legend>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '35% minmax(0, 1fr)', gap: 1, alignItems: 'center' }}>
                            <Box sx={{ textAlign: 'left', pr: 1 }}>
                                <span style={{ color: 'red', marginRight: 4 }}>*</span>Dossier de prêt
                            </Box>
                            <Box sx={{ minWidth: 0, width: '100%' }}>
                                <SearchInput width={0} form={form} Titre='Recherche dossier' label='' name='NUMDOS' Parametre={searchPret} handleFunction={consult} />
                            </Box>
                        </Box>
                    </fieldset>
                    {pret && (
                        <Descriptions size="small" column={2} bordered style={{ marginTop: 12 }}>
                            <Descriptions.Item label="Client">{pret.NOMCLI} ({pret.NUMCLI})</Descriptions.Item>
                            <Descriptions.Item label="Type de prêt">{pret.TYPEPRET}</Descriptions.Item>
                            <Descriptions.Item label="Montant initial">{formatNumber(pret.MONTAN)}</Descriptions.Item>
                            <Descriptions.Item label="Taux intérêt">{pret.TAUXINT}%</Descriptions.Item>
                            <Descriptions.Item label="Taux pénalité">{pret.TAUXPEN}%</Descriptions.Item>
                            <Descriptions.Item label="Amortissement">{pret.LIBTYPAMORT ?? pret.TYPAMORT}</Descriptions.Item>
                            <Descriptions.Item label="Échéances">{pret.NBRECHE}</Descriptions.Item>
                            <Descriptions.Item label="Solde capital restant">{formatNumber(pret.RESTANTDU ?? 0)}</Descriptions.Item>
                            <Descriptions.Item label="Statut">{pret.LIBETAT}</Descriptions.Item>
                            <Descriptions.Item label="Éch. payées">{`${pret.NBECHPAYE ?? 0} — ${formatNumber(pret.MTNECHPAYE ?? 0)}`}</Descriptions.Item>
                            <Descriptions.Item label="Éch. non payées">{`${(pret.NBECHTOT ?? 0) - (pret.NBECHPAYE ?? 0)} — ${formatNumber(pret.MTNECHIMPAYE ?? 0)}`}</Descriptions.Item>
                        </Descriptions>
                    )}
                    {pret && (
                        <fieldset className="bordure1" style={{ marginTop: 12, width: '100%' }}>
                            <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Tableau d'amortissement actuel</legend>
                            {echeances.length === 0 ? (
                                <Typography variant="caption" color="textSecondary">Aucun échéancier pour ce dossier.</Typography>
                            ) : (
                                <Table size='small' columns={colsEchActuel} dataSource={echeances}
                                    scroll={{ x: 700, y: 250 }} pagination={false} rowKey="NUMSEQ" />
                            )}
                        </fieldset>
                    )}
                </div>
            )
        },
        {
            key: '2',
            label: <span><FormOutlined /> Dossier & scénarios</span>,
            children: (
                <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                    {pret ? (
                        <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                            <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Dossier de restructuration</legend>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                                <SearchModal width={9} form={form} Titre='Motif' label='Motif de restructuration' name='MOTIF' Parametre={searchMotif} />
                                <Form.Item name="COMMENTAIRE" label="Commentaire"><Input /></Form.Item>
                            </Box>
                            <Form.Item name="DIAGNOSTIC" label="Diagnostic financier (résumé)">
                                <Input.TextArea rows={2} maxLength={500} />
                            </Form.Item>
                        </fieldset>
                    ) : (
                        <Alert type="info" showIcon message="Consultez d'abord un prêt dans l'onglet « Prêt concerné »." />
                    )}

                    {dossier && typePret && (
                        <fieldset className="bordure1" style={{ marginTop: 12, width: '100%' }}>
                            <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Nouveau scénario de négociation</legend>
                            <Alert
                                style={{ marginBottom: 10 }}
                                type={typePret.RESTRUCTURE ? 'info' : 'error'} showIcon
                                message={typePret.RESTRUCTURE
                                    ? `Conditions du type de prêt ${typePret.TYPEPRET} : max ${typePret.nbrRestruMax ?? '∞'} restructuration(s), délai min. ${typePret.delaiMinRestru ?? 0} mois, ${typePret.nbMinRestruPayees ?? 0} échéances payées min.` +
                                      (typePret._TERME ? `, durée ${typePret._TERME.DUREEMIN}-${typePret._TERME.DUREEMAX ?? '∞'} mois, max ${typePret._TERME.NBRECHMAX} échéances` : '') +
                                      (typePret.isRestruInt ? ', intérêts capitalisés' : '') +
                                      (typePret.isRestruPen ? ', pénalités capitalisées' : '') +
                                      (typePret.isRestruTaux ? `, taux renégociable (max ${typePret.tauxRestruMax ?? '∞'}%)` : ', taux non renégociable') +
                                      (typePret.isRestruTypamort ? ', amortissement modifiable' : ', amortissement figé')
                                    : `Le type de prêt ${typePret.TYPEPRET} n'autorise pas la restructuration`} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                                <Form.Item name="LIBSCEN" label="Libellé du scénario" rules={[{ required: true, message: 'Obligatoire' }]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item name="NBRECHE_SCEN" label="Nb échéances" rules={[
                                    { required: true, message: 'Obligatoire' },
                                    ...(typePret._TERME?.NBRECHMAX
                                        ? [ruleMaxValue(Number(typePret._TERME.NBRECHMAX), "Nombre d'échéances")]
                                        : [])]}>
                                    <InputNumber style={{ width: '100%' }} min={1} />
                                </Form.Item>
                                <SearchModal width={9} form={form} Titre='Périodicité' label='Périodicité' name='PERIODIC_SCEN' Parametre={searchPeriod} />
                                <Form.Item name="DAT1ECHE_SCEN" label="1ère échéance" rules={[{ required: true, message: 'Obligatoire' }]}>
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                </Form.Item>
                                <Form.Item name="TAUXINT_SCEN" label="Taux intérêt négocié (%)"
                                    tooltip={typePret.isRestruTaux ? `Max ${typePret.tauxRestruMax ?? '∞'}%` : 'Taux non renégociable pour ce type de prêt'}
                                    rules={typePret.isRestruTaux && typePret.tauxRestruMax != null
                                        ? [ruleMaxValue(Number(typePret.tauxRestruMax), 'Taux négocié')] : []}>
                                    <InputNumber style={{ width: '100%' }} step={0.25} min={0}
                                        disabled={!typePret.isRestruTaux} />
                                </Form.Item>
                                <Form.Item name="TAUXPEN_SCEN" label="Taux pénalité (%)"
                                    tooltip={typePret.isRestruTaux ? '' : 'Non renégociable pour ce type de prêt'}>
                                    <InputNumber style={{ width: '100%' }} step={0.25} min={0}
                                        disabled={!typePret.isRestruTaux} />
                                </Form.Item>
                                <Form.Item name="TYPAMORT_SCEN" label="Type d'amortissement"
                                    tooltip={typePret.isRestruTypamort ? '' : 'Type figé pour ce type de prêt'}>
                                    <Select disabled={!typePret.isRestruTypamort} options={[
                                        { value: 'C', label: 'Amortissement constant' },
                                        { value: 'V', label: 'Annuité constante' },
                                        { value: 'I', label: 'In fine' }]} />
                                </Form.Item>
                                <Form.Item name="MNTDECONDONNE" label="Remise décondonnée">
                                    <InputNumber style={{ width: '100%' }} min={0} />
                                </Form.Item>
                                <Form.Item name="COMENTSCEN" label="Commentaire scénario">
                                    <Input />
                                </Form.Item>
                            </Box>
                        </fieldset>
                    )}

                    {scenarios.length > 0 && (
                        <fieldset className="bordure1" style={{ marginTop: 12, width: '100%' }}>
                            <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Scénarios négociés ({scenarios.length}) — sélectionnez pour modifier</legend>
                            <Table size='small' columns={colsScen} dataSource={scenarios} pagination={false} rowKey="Id"
                                scroll={{ x: 900, y: 160 }}
                                rowSelection={{
                                    type: 'radio', selectedRowKeys: scenSel !== null ? [scenSel] : [],
                                    onChange: (keys) => {
                                        const k = keys[0] ?? null;
                                        setScenSel(k);
                                        const s = scenarios.find(x => x.Id === k);
                                        if (s) form.setFieldsValue({
                                            LIBSCEN: s.LIBSCEN, NBRECHE_SCEN: s.NBRECHE, PERIODIC_SCEN: s.PERIODIC,
                                            lib_PERIODIC_SCEN: s.PERIODIC,
                                            DAT1ECHE_SCEN: s.DAT1ECHE ? dayjs(String(s.DAT1ECHE).substring(0, 10)) : null,
                                            TAUXINT_SCEN: s.TAUXINT, TAUXPEN_SCEN: s.TAUXPEN,
                                            TYPAMORT_SCEN: s.TYPAMORT, MNTDECONDONNE: s.MNTDECONDONNE,
                                            COMENTSCEN: s.COMMENTAIRE
                                        });
                                    }
                                }} />
                        </fieldset>
                    )}

                    {preview.length > 0 && (
                        <fieldset className="bordure1" style={{ marginTop: 12, width: '100%' }}>
                            <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Aperçu échéancier (non enregistré)</legend>
                            <Table size='small' columns={colsEch} dataSource={preview} scroll={{ x: 700, y: 250 }} pagination={false} rowKey="numSeq" />
                        </fieldset>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b>Analyse de restructuration</b></Typography>
            <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
                <Form form={form} layout="vertical" labelAlign='left'>
                    <div className='bG'>
                        <legend><Typography color={"#116599"}>Actions</Typography></legend>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Button size='large' className='bt_add' onClick={consult} type="ghost" htmlType="button" block>1. Consulter le prêt</Button>
                            <Divider style={{ margin: '2px 0', borderColor: '#116599' }} />
                            <Button size='large' className='bt_add' disabled={!pret || !!dossier?.Id} onClick={handleDossier} type="ghost" htmlType="button" block>2. Créer le dossier</Button>
                            <Button size='large' className='bt_add' disabled={!dossier?.Id} onClick={handleScenario} type="ghost" htmlType="button" block>{scenSel ? "3. Modifier le scénario" : "3. Ajouter un scénario"}</Button>
                            <Button size='large' className='bt_add' disabled={!dossier?.Id} onClick={handlePreview} type="ghost" htmlType="button" block>4. Aperçu échéancier</Button>
                            <Divider style={{ margin: '2px 0', borderColor: '#116599' }} />
                            <Button size='large' className='bt_add' disabled={!dossier?.Id || scenarios.length === 0} onClick={handleSoumettre} type="ghost" htmlType="button" block>5. Soumettre</Button>
                            <Button size='large' className='bt_add' onClick={() => { form.resetFields(); setPret(null); setDossier(null); setScenarios([]); setEcheances([]); setPreview([]); setScenSel(null); }} type="ghost" htmlType="reset" block>Réinitialiser</Button>
                        </Space>
                        {dossier && (
                            <Tag color="gold" style={{ marginTop: 10, whiteSpace: 'normal' }}>
                                Dossier #{dossier.Id} — {dossier.CDETAT ?? 'BROUILLON'}
                            </Tag>
                        )}
                    </div>
                    <div className='clientP'>
                        <div style={{ width: '88%' }}>
                            <Tabs size='small' type="card" defaultActiveKey="1" tabBarGutter={8}
                                tabBarStyle={{ overflowX: 'auto', whiteSpace: 'nowrap' }} items={tabsItems} />
                        </div>
                    </div>
                </Form>
            </Spin>
            {typePretModal}
        </div>
    );
};

export default AnalyseRestructuration;
